import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { m } from 'motion/react';
import { Sparkles, UserPlus, Users } from 'lucide-react';

import { HireFromTemplate } from './HireFromTemplate';
import { employees } from '../../api/organization';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorBanner } from '../../components/ErrorBanner';
import { fieldStyles } from '../../components/styles';
import { SkeletonRows } from '../../components/Skeleton';
import { PageHeader } from '../../components/Surface';
import { RISE, STAGGER } from '../../components/motion';
import { useT } from '../../i18n/provider';
import type { Key } from '../../i18n/provider';
import { cn } from '../../lib/cn';
import { useCompanyId, useCompanySlug } from '../company/useCompany';

const STATUSES = ['active', 'pending_approval', 'paused', 'suspended', 'terminated'] as const;

/**
 * `13 §3.2`: la plantilla de personal.
 *
 * La acción principal es contratar desde una plantilla y no el asistente en
 * blanco, y ese orden es el criterio de aceptación de esta fase: seis empleados
 * en diez minutos solo ocurre si el camino por defecto es el que llega ya
 * escrito.
 */
export function EmployeesScreen() {
  const t = useT();
  const companyId = useCompanyId();
  const companySlug = useCompanySlug();
  const [status, setStatus] = useState<string>('');
  const [hiring, setHiring] = useState(false);

  const params = new URLSearchParams({ limit: '50' });
  if (status !== '') {
    params.set('filter[status]', status);
  }

  const list = useQuery({
    queryKey: ['employees', companyId, status],
    queryFn: () => employees.list(companyId, params),
    enabled: companyId !== '',
  });

  return (
    <section className="space-y-6">
      <PageHeader
        title={t('employees.title')}
        description={t('employees.intro')}
        actions={
          <>
            <Link to="/c/$companySlug/employees/new" params={{ companySlug }}>
              <Button variant="secondary">{t('employees.startBlank')}</Button>
            </Link>
            <Link to="/c/$companySlug/employees/templates" params={{ companySlug }}>
              <Button variant="secondary" icon={<Sparkles />}>
                {t('templates.manage')}
              </Button>
            </Link>
            <Button icon={<UserPlus />} onClick={() => setHiring(true)}>
              {t('employees.hireFromTemplate')}
            </Button>
          </>
        }
      />

      {hiring ? <HireFromTemplate onClose={() => setHiring(false)} /> : null}

      <div className="flex items-center gap-2">
        <label htmlFor="status" className="text-sm text-ink-soft">
          {t('employees.status')}
        </label>
        <select
          id="status"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          className={cn(fieldStyles, 'w-auto border-line px-2 py-1.5 text-sm')}
        >
          <option value="">{t('common.all')}</option>
          {STATUSES.map((value) => (
            <option key={value} value={value}>
              {t(`employees.statuses.${value}` as Key)}
            </option>
          ))}
        </select>
      </div>

      {list.isPending ? <SkeletonRows /> : null}
      {list.error ? <ErrorBanner error={list.error} onRetry={() => void list.refetch()} /> : null}

      {list.data && list.data.data.length === 0 ? (
        <EmptyState
          icon={<Users />}
          title={t('employees.nobodyYet')}
          description={t('employees.nobodyYetHint')}
          // No «contratar desde una plantilla» otra vez: dos botones con el
          // mismo nombre accesible en una pantalla obligan a quien navega por
          // nombre —un lector de pantalla, o un test— a desambiguar por
          // posición.
          action={<Button onClick={() => setHiring(true)}>{t('employees.hireFirst')}</Button>}
        />
      ) : null}

      {list.data && list.data.data.length > 0 ? (
        <m.ul
          variants={STAGGER}
          initial="hidden"
          animate="shown"
          className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-raised"
        >
          {list.data.data.map((employee) => (
            <m.li
              key={employee.id}
              variants={RISE}
              className="flex items-center gap-4 px-4 py-3 transition-colors hover:bg-hover"
            >
              <span className="brand-gradient grid size-9 shrink-0 place-items-center rounded-sm text-xs font-extrabold text-white">
                {employee.name.charAt(0).toUpperCase()}
              </span>

              <div className="min-w-0 grow">
                <Link
                  to="/c/$companySlug/employees/$employeeSlug"
                  params={{ companySlug, employeeSlug: employee.slug }}
                  className="text-sm font-semibold text-ink transition-colors hover:text-primary"
                >
                  {employee.name}
                </Link>
                <p className="truncate text-xs text-ink-faint">
                  {employee.job_title} · {employee.seniority} ·{' '}
                  {t('employees.level', { level: employee.hierarchy_level })}
                </p>
              </div>

              {/* Sin ficha publicada no puede trabajar: el orquestador bloquea
                  su primera tarea diciéndolo. Este es el sitio donde verlo
                  antes de que eso pase. A quien ya salió de la empresa no se
                  le reprocha la ficha. */}
              {!employee.has_published_profile && employee.status !== 'terminated' ? (
                <Badge tone="warning" size="sm">
                  {t('employees.unpublishedCard')}
                </Badge>
              ) : null}

              <Badge tone={employee.status === 'active' ? 'success' : employee.status === 'terminated' ? 'neutral' : 'warning'} size="sm">
                {t(`employees.statuses.${employee.status}` as Key)}
              </Badge>
            </m.li>
          ))}
        </m.ul>
      ) : null}
    </section>
  );
}
